import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Zap, Shield, Cpu, Activity, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";

const FLOW = [
  { label: "Accident", tone: "bg-rose-500" },
  { label: "AI Intimation", tone: "bg-orange-500" },
  { label: "Survey", tone: "bg-amber-500" },
  { label: "Approval", tone: "bg-sky-500" },
  { label: "Cashless Repair", tone: "bg-blue-700" },
  { label: "Settlement", tone: "bg-emerald-500" },
];

export default function Hero() {
  const { t } = useI18n();
  return (
    <section className="relative overflow-hidden mesh-hero" data-testid="hero-section">
      {/* Aurora glow blobs */}
      <div className="aurora animate-aurora" style={{ width: 520, height: 520, top: -180, left: -120, background: "radial-gradient(circle, #4F46E5 0%, transparent 60%)" }} />
      <div className="aurora animate-aurora" style={{ width: 460, height: 460, top: -100, right: -140, background: "radial-gradient(circle, #06B6D4 0%, transparent 60%)", animationDelay: "6s" }} />
      <div className="aurora animate-aurora" style={{ width: 360, height: 360, bottom: -140, left: "40%", background: "radial-gradient(circle, #E11D74 0%, transparent 60%)", animationDelay: "12s" }} />

      <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-semibold text-slate-700 dark:text-slate-200 mb-6 shadow-sm"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span>{t("hero.pill")}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02] text-slate-900 dark:text-white"
            >
              {t("hero.title.a")} <span className="gradient-text">{t("hero.title.b")}</span><br />
              {t("hero.title.c")} <span className="relative inline-block">
                <span className="relative z-10">{t("hero.title.d")}</span>
                <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 200 8" preserveAspectRatio="none">
                  <path d="M2,6 Q100,-2 198,4" stroke="url(#underline)" strokeWidth="3" fill="none" strokeLinecap="round" />
                  <defs>
                    <linearGradient id="underline" x1="0" x2="1">
                      <stop offset="0" stopColor="#4F46E5" />
                      <stop offset="1" stopColor="#06B6D4" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-5 text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed"
            >
              {t("hero.subline")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
              data-testid="hero-cta-group"
            >
              <Link
                to="/file-claim"
                className="btn-signature rounded-xl px-6 py-3.5 font-semibold flex items-center justify-center gap-2"
                data-testid="hero-file-claim"
              >
                {t("cta.startClaim")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/enterprise"
                className="btn-ghost-outline rounded-xl px-6 py-3.5 font-semibold flex items-center justify-center gap-2"
                data-testid="hero-enterprise"
              >
                {t("cta.forEnterprises")} <Sparkles className="w-4 h-4" />
              </Link>
            </motion.div>

            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-600 dark:text-slate-400">
              <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-blue-700" /> {t("hero.badge.assisted")}</div>
              <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-orange-500" /> {t("hero.badge.settlement")}</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-pink-500" /> {t("hero.badge.garages")}</div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              {/* Live product screen */}
              <div className="glass-strong rounded-3xl p-6 relative overflow-hidden shadow-2xl shadow-blue-700/10">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                      <Cpu className="w-3 h-3" /> MICA · Live Claim
                    </div>
                    <div className="font-heading font-extrabold text-lg text-slate-900 dark:text-white">Claim #MI29841</div>
                  </div>
                  <div className="px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
                  </div>
                </div>

                <div className="space-y-2.5">
                  {FLOW.map((step, i) => (
                    <motion.div
                      key={step.label}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + i * 0.08 }}
                      className="flex items-center gap-3"
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs shadow-md shrink-0 ${step.tone}`}>
                        {i + 1}
                      </div>
                      <div className="flex-1 bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-2 border border-slate-100 dark:border-slate-700/50">
                        <div className="font-semibold text-slate-900 dark:text-slate-100 text-sm">{step.label}</div>
                      </div>
                      <div className={`w-2 h-2 rounded-full ${i <= 3 ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`} />
                    </motion.div>
                  ))}
                </div>

                <div className="mt-5 p-3 rounded-xl bg-gradient-to-r from-blue-800 to-blue-950 text-white relative overflow-hidden">
                  <div className="absolute inset-0 opacity-30 animate-shimmer" />
                  <div className="relative">
                    <div className="text-[10px] font-bold uppercase tracking-wider opacity-80 flex items-center gap-1.5">
                      <Activity className="w-3 h-3" /> AI settlement forecast
                    </div>
                    <div className="font-heading font-extrabold text-2xl">₹1,42,300 · 12 days</div>
                  </div>
                </div>
              </div>

              {/* Floating badges */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="absolute -top-4 -right-4 glass-strong rounded-2xl px-3 py-2 shadow-xl border border-blue-700/20"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-700 to-orange-500 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Gemini Vision</div>
                    <div className="text-xs font-bold text-slate-900 dark:text-white">96% accuracy</div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 }}
                className="absolute -bottom-4 -left-4 glass-strong rounded-2xl px-3 py-2 shadow-xl border border-emerald-500/20"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-lime-500 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Settlement</div>
                    <div className="text-xs font-bold text-slate-900 dark:text-white">₹142 Cr paid</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
